import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  SearchBar,
  SectionHeader,
  TableActionTypes,
  TABLE_INITIAL_STATE,
  useDataTable,
} from 'web-library';
import { KeyValueResponse } from '../../../../api';
import { usePostGenerateNonNationalResultFile } from '../../../../api-refactored/candidate/usePostGenerateNonNationalResultFile';
import { usePostNonNationalSearchResultRelease } from '../../../../api-refactored/candidate/usePostNonNationalSearchResultRelease';
import { RetrieveS3URLResponse } from '../../../../api/document/files/api-services/model';
import * as QUERIES from '../../../../api/document/files/queries-services/queries';
import ExportResultProgressModal from '../../../../components/modals/ExportResultProgessModal';
import { useTriggerMutate } from '../../../../hooks/query';
import { FileUploadState } from '../../../../models/File';
import NonNationalExamGroupsButton from '../../../admin/components/ResultRelease/NonNationalExamGroupsButton';
import NonNationalExamGroupsExportResultModal from '../../../admin/components/ResultRelease/NonNationalExamGroupsExportResultModal';
import ImportResultsModal from './ImportResultsModal';

export interface SnackBarConfig {
  open: boolean;
  message: string;
}

const useStyles = makeStyles()((theme) =>
({
  titleRoot: {
    lineHeight: 1.3,
  },
  dataTableRoot: {
    width: '100%',
  },
  dialogContainer: {
    display: 'flex',
    margin: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogContent: {
    padding: theme.spacing(1),
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  viewDetailsButton: {
    color: theme.color.link.linkDefault01,
  },
  headerContainer: {
    paddingBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      paddingBottom: theme.spacing(1),
    },
  },
  addCandidateButton: {
    minWidth: '200px',
  },
  examGroup: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}));

/** define default columns
 * */
const customTableColumns = (t: TFunction): MUIDataTableColumnDef[] => [
  {
    name: 'examGroup',
    label: t('exam->>NonNationalExam.ExamGroup'),
  },
  {
    name: 'candidateRegId',
    label: t('exam->>NonNationalExam.CandidateRegId'),
  },
  {
    name: 'candidateName',
    label: t('exam->>NonNationalExam.Name'),
  },
  {
    name: 'indexNo',
    label: t('exam->>NonNationalExam.IndexNo'),
  },
  {
    name: 'candidateType',
    label: t('exam->>NonNationalExam.CandidateType'),
  },
  {
    name: 'placementLevel',
    label: t('exam->>NonNationalExam.PlacementLevel'),
  },
];

const OrgAdminNonNationalResultRelease = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [fileUUID, setFileUUID] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );
  const [selectExamGroup, setSelectExamGroup] = useState<KeyValueResponse | undefined>(undefined);
  const [isOpenDialogExport, setIsOpenDialogExport] = useState<boolean>(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    size: 10,
  });

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      selectableRows: 'none',
      pagination: true,
      filter: false,
      filterType: 'multiselect',
      selectToolbarPlacement: 'replace',
    }),
    [tOptions]
  );

  const { isLoading, mutate } = usePostNonNationalSearchResultRelease({
    onSuccess: (response) => {
      if (response) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: response.content,
            count: response.totalElements,
          },
        } as TableActionTypes);
      }
    },
    onError: () => null,
  });

  const callMutation = useCallback(() => {
    if (state.search && state.search.toString().length >= 3) {
      mutate({ page: state.page, size: state.size, keywords: state.search });
    } else {
      mutate({ page: state.page, size: state.size, keywords: '' });
    }
  }, [mutate, state.page, state.search, state.size]);
  const setTriggerMutate = useTriggerMutate(callMutation);

  useEffect(() => {
    setTriggerMutate((prev) => prev + 1);
  }, [state.search, state.page, state.size, setTriggerMutate]);

  QUERIES.useGetRetrieveS3PreSignUrl(
    { data: fileUUID, url: '/api/s3/files' },
    {
      cacheTime: 0,
      retry: false,
      staleTime: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: fileUUID !== '',
      refetchInterval: 5000,
      onSuccess: (response) => {
        if (response.fileStatus === FileUploadState.SUCCESS && fileUUID !== '') {
          const newWindow = window.open(response.url, '_blank', 'noopener,noreferrer');
          if (newWindow) {
            newWindow.opener = null;
          }
        }
        handleDownloadTemplate(response);
      },
    }
  );

  const { mutate: exportResultMutate } = usePostGenerateNonNationalResultFile({
    onSuccess: (response) => {
      setFileUUID(response.uuid);
    },
    onError: () => null,
  });

  const handleBulkDownload = useCallback(
    (selectedExam: KeyValueResponse) => {
      exportResultMutate(selectedExam);
    },
    [exportResultMutate]
  );

  const onDialogExist = () => {
    setFileUUID('');
    setDialogOpen(false);
  };

  const handleDownloadTemplate = (response: RetrieveS3URLResponse) => {
    if (response.fileStatus === FileUploadState.SUCCESS) {
      setFileUUID('');
      setDialogOpen(false);
      handleCancelDialogExport();
    }
    setFileDownloadStatus(response.fileStatus as FileUploadState);
  };

  const handleSelectExamGroup = (selectedOption: KeyValueResponse) => {
    setSelectExamGroup(selectedOption);
  };

  const handleOpenDialogExport = () => {
    setIsOpenDialogExport(true);
  };

  const handleOpenDialogImport = () => {
    setImportDialogOpen(true);
  };

  const handleCancelDialogExport = () => {
    setIsOpenDialogExport(false);
    setSelectExamGroup(undefined);
  };

  return (
    <>
      <Box className={classes.headerContainer}>
        <SectionHeader title={t('exam->>NonNationalExam.CandidateResultsList')} />
        <NonNationalExamGroupsButton
          classes={classes}
          onOpenDialogExport={handleOpenDialogExport}
          onOpenDialogImport={handleOpenDialogImport}
        />
      </Box>
      <Box className={classes.searchContainer}>
        <SearchBar
          placeholder={t('exam->>NonNationalExam.SearchCandidateRegIdNameIndexNo')}
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue={state.search}
          onSearch={(str) => {
            tableDispatch({
              type: 'UPDATE_SEARCH',
              payload: str,
            });
          }}
        />
      </Box>
      <MUIDataTable
        isLoading={isLoading}
        // @ts-ignore
        state={state}
        dispatch={tableDispatch}
        columns={customTableColumns(t)}
        // @ts-ignore
        data={state.data}
        options={customOptions}
        rowsPerPageOptions={[10, 20, 30, 40, 50]}
      />
      <NonNationalExamGroupsExportResultModal
        classes={classes}
        isOpenDialogExport={isOpenDialogExport}
        onCancelDialogExport={handleCancelDialogExport}
        selectExamGroup={selectExamGroup}
        setDialogOpen={setDialogOpen}
        onBulkDownload={handleBulkDownload}
        onCloseDialogExport={() => {
          setIsOpenDialogExport(false);
          setFileDownloadStatus(FileUploadState.IDLE);
        }}
        onSelectExamGroup={handleSelectExamGroup}
      />
      <ExportResultProgressModal
        fileDownloadStatus={fileDownloadStatus}
        modalState={dialogOpen}
        onDialogClose={onDialogExist}
      />
      <ImportResultsModal
        modelOpen={importDialogOpen}
        onClose={() => {
          setImportDialogOpen(false);
        }}
        handlReloadData={setTriggerMutate}
      />
    </>
  );
};
export default OrgAdminNonNationalResultRelease;
